import type { AssistantCard, MyBusiness } from "@teamna/shared";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useAssistantCards, useOpenAssistantCard } from "@teamna/web-client/hooks/use-assistant.ts";
import { Button, InlineError, Panel, SkeletonList, SparkIcon } from "../../components/index.ts";
import { ChatStatusPill } from "./assistant-blocks.tsx";
import { AssistantChat } from "./chat.tsx";

/**
 * "Suggestions by AI Assistant" on Home (strategy repo `teamna/strategy/ai-assistant-v1.md`, section 8,
 * prototype "Talk It Through"): under the To do list, the things AI Assistant found that morning. The
 * header is quiet (a grey band with a small sparkle), so the card does not shout over To do.
 *
 * A tap opens the suggestion in a side sheet over Home: the same chat as the AI Assistant page, with
 * "Open in AI Assistant". Each open checks the suggestion again on the server, so the sheet never
 * shows a plan for something that is not true any more. The card is not there when there is nothing
 * to suggest.
 *
 * `readOnly` is Control Center, acting as the business: the same list, but a suggestion does not
 * open, because the first open makes a chat for the owner. A suggestion that somebody opened links
 * to its chat in AI Assistant review, in a new tab, so the act-as frame stays open.
 */
export type SuggestionsReadOnly = {
  /** The chat in AI Assistant review. Each open there writes an audit row with the staff email. */
  chatHref: (conversationId: string) => string;
  /** All the chats of the business in AI Assistant review. */
  reviewHref: string;
};

const ROW = "flex w-full flex-wrap items-center gap-x-4 gap-y-1.5 px-5 py-3.5 text-left";

export function SuggestionsCard({ business, readOnly }: { business: MyBusiness; readOnly?: SuggestionsReadOnly }) {
  const cards = useAssistantCards(business.businessId);
  const open = useOpenAssistantCard(business.businessId);
  const navigate = useNavigate();
  const [sheet, setSheet] = useState<AssistantCard | null>(null);

  const items = cards.data ?? [];
  if (items.length === 0 && !sheet) return null;

  function show(card: AssistantCard) {
    setSheet(card);
    open.mutate({ cardId: card.id, entry: "home_sheet" });
  }

  const conversationId = open.data && open.variables?.cardId === sheet?.id ? open.data.id : null;

  return (
    <section aria-labelledby="suggestions-title" className="overflow-hidden rounded-card border border-line bg-paper shadow-card">
      <div className="flex items-center gap-2 border-b border-line bg-sunken/70 px-5 py-2">
        <SparkIcon size={14} className="text-quiet" />
        <h2 id="suggestions-title" className="text-[13px] font-semibold tracking-[0.02em] text-ink-2">
          Suggestions by AI Assistant
        </h2>
      </div>
      <ol>
        {items.map((card, index) => (
          <li key={card.id} className={index > 0 ? "border-t border-line" : undefined}>
            {!readOnly ? (
              <button type="button" onClick={() => show(card)} className={`${ROW} hover:bg-sunken/50`}>
                <SuggestionRow card={card} />
              </button>
            ) : card.conversationId ? (
              <a href={readOnly.chatHref(card.conversationId)} target="_blank" rel="noreferrer" className={`${ROW} hover:bg-sunken/50`}>
                <SuggestionRow card={card} />
              </a>
            ) : (
              <div className={ROW}>
                <SuggestionRow card={card} />
              </div>
            )}
          </li>
        ))}
      </ol>
      {readOnly ? (
        <p className="border-t border-line px-5 py-3 text-[13.5px] text-ink-2">
          You cannot open a suggestion while you act as the business: the first open makes a chat for the owner.{" "}
          <a href={readOnly.reviewHref} target="_blank" rel="noreferrer" className="font-semibold text-accent-strong hover:underline">
            Read the chats in AI Assistant review
          </a>
        </p>
      ) : null}

      <Panel open={sheet !== null} onClose={() => setSheet(null)} title={sheet?.title ?? ""} subtitle={sheet?.subtitle} width="lg" flush>
        {open.isPending ? (
          <div className="px-5 py-4">
            <SkeletonList rows={3} />
          </div>
        ) : open.error ? (
          <div className="px-5 py-4">
            <InlineError error={open.error} />
          </div>
        ) : conversationId ? (
          <AssistantChat
            key={conversationId}
            business={business}
            conversationId={conversationId}
            variant="sheet"
            headExtra={
              <Button size="sm" variant="ghost" onClick={() => void navigate({ to: `/owner/assistant/${conversationId}` })}>
                Open in AI Assistant
              </Button>
            }
          />
        ) : null}
      </Panel>
    </section>
  );
}

function SuggestionRow({ card }: { card: AssistantCard }) {
  return (
    <>
      <span className="min-w-0 flex-1">
        <span dir="auto" className="block text-[15px] font-semibold text-ink">
          {card.title}
        </span>
        <span dir="auto" className="block text-[13.5px] text-ink-2">
          {card.subtitle}
        </span>
      </span>
      <span className="flex items-center gap-2">
        {card.tag ? <span className="rounded-pill bg-late-surface px-2 py-0.5 text-[11.5px] font-semibold text-late">{card.tag}</span> : null}
        <ChatStatusPill status={card.chatStatus} />
      </span>
    </>
  );
}
